import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchList } from '@/lib/api';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Artists', to: '/artists' },
  { label: 'Videos', to: '/videos' },
  { label: 'Newsletters', to: '/newsletters' },
  { label: 'Shop', external: true },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shopUrl, setShopUrl] = useState('');
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // refetch on route change + window focus so admin edits appear without a hard reload
    const refresh = () => fetchList('/settings').then((s) => setShopUrl(s?.shop_url || '')).catch(() => {});
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [location.pathname]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const navCls = (isActive) =>
    `link-sweep meta-label ${isActive ? 'is-active text-brayo-blue' : 'text-brayo-charcoal/80'} hover:text-brayo-charcoal`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled ? 'bg-brayo-paper/90 backdrop-blur-md border-b border-brayo-line' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1500px] items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="logo-link" aria-label="Brayo Records home" className="flex items-center">
            <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className="h-9 w-auto md:h-11" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              item.external && shopUrl ? (
                <a
                  key={item.label}
                  data-testid="nav-link-shop"
                  href={shopUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-sweep meta-label text-brayo-charcoal/80 hover:text-brayo-charcoal"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.external ? '/shop' : item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) => navCls(isActive)}
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 flex-col items-center justify-center gap-[6px] lg:hidden"
          >
            <span className={`block h-[1.5px] w-6 bg-brayo-charcoal transition-transform duration-300 ${open ? 'translate-y-[7.5px] rotate-45' : ''}`} />
            <span className={`block h-[1.5px] w-6 bg-brayo-charcoal transition-opacity duration-300 ${open ? 'opacity-0' : ''}`} />
            <span className={`block h-[1.5px] w-6 bg-brayo-charcoal transition-transform duration-300 ${open ? '-translate-y-[7.5px] -rotate-45' : ''}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-30 flex flex-col justify-center bg-brayo-paper px-8 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="flex flex-col gap-3">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  {item.external && shopUrl ? (
                    <a data-testid="mobile-nav-link-shop" href={shopUrl} target="_blank" rel="noopener noreferrer" className="display-title text-5xl text-brayo-charcoal">
                      {item.label}
                    </a>
                  ) : (
                    <NavLink
                      to={item.external ? '/shop' : item.to}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `display-title text-5xl ${isActive ? 'text-brayo-blue' : 'text-brayo-charcoal'}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
            </nav>
            <p className="meta-label text-brayo-dim mt-14">Brayo Records — Independent Label</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}